/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-console */
import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ModalController, NavController, Platform } from '@ionic/angular';
import { BarcodeScanner, ScanResult, SupportedFormat } from '@capacitor-community/barcode-scanner';
import { regexPatternForConnectionUrl, ToastDuration, ToastService, LoadingIndicatorState } from '@shared';
import { ApiConnectionsRepository, WorkspaceIdGeneratorService, WorkspaceRepository } from '@database/central';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ConnectionsModalComponentsEnum, ConnectionTypeEnum } from '../../models';
import { ConnectionsModalState } from '../../services';
import { DOCUMENT } from '@angular/common';
import { ExternalTransferApiService } from '@api/external-transfer';
import { HttpStatusCode } from '@angular/common/http';
import { ConnectionsListComponent } from '../list/list.component';

@Component({
	selector: 'app-connections-qr-code',
	templateUrl: 'qr-code.component.html',
	styleUrls: ['qr-code.component.scss']
})
export class ConnectionsQrCodeComponent implements OnInit, OnDestroy {
	@Input() public connectionType: ConnectionTypeEnum;
	public displayBackButton$: Observable<boolean> = this.workfileRepository.hasWorkfilesImported$();

	private backButtonSubscription: Subscription;
	public isAPIUrl: boolean = false

	public constructor(
		private navController: NavController,
		private workfileRepository: WorkspaceRepository,
		private toastService: ToastService,
		private translateService: TranslateService,
		private workspaceIdGeneratorService: WorkspaceIdGeneratorService,
		private apiConnectionsRepository: ApiConnectionsRepository,
		private modalCtrl: ModalController,
		private connectionsModalState: ConnectionsModalState,
		private externalTransferApiService: ExternalTransferApiService,
        private loadingIndicator: LoadingIndicatorState,
		@Inject(DOCUMENT) private document: Document,
		private platform: Platform
	) {}

	public async ngOnInit(): Promise<void> {
		await this.platform.ready(); 
		this.startPrepareLayoutForScan();
		this.handleBackButton();
		await this.scanQrCode();
	}

	private handleBackButton(): void {
		this.backButtonSubscription = this.platform.backButton.subscribeWithPriority(10, () => {
			this.loadingIndicator.setIsLoading(false);
			this.goBack();
		});
	}

	public async scanQrCode(): Promise<void> {
		await BarcodeScanner.checkPermission({ force: true });
		const timeoutPromise = new Promise(resolve => {
			setTimeout(() => {
			  resolve(null); // Resolve with null after 20 seconds
			}, 10000);
		  });

		

		const result= await Promise.race([ BarcodeScanner.startScan({ targetedFormats: [SupportedFormat.QR_CODE] }), timeoutPromise ]);

		if(result !== null) {
			this.loadingIndicator.setIsLoading(true);
			const scanningResult : ScanResult = result as ScanResult;
			
			if(scanningResult.hasContent) {
				this.validateQrCode(scanningResult);
			}

		}
	}

	public ngOnDestroy(): void {
		this.stopScan();
		if (this.backButtonSubscription) {
			this.backButtonSubscription.unsubscribe();
		}
	}

	public goBack(): void {
		this.navController.pop();
	}

	private stopScan(): void {
		this.endPrepareLayoutForScan();
		BarcodeScanner.stopScan();
	}

	// This component is used in two places and certain elements have to be hidden in order to see the camera feed
	private startPrepareLayoutForScan(): void {
		this.document.getElementsByTagName('html')[0].style.backgroundColor = 'transparent';
		this.document.getElementsByTagName('ion-router-outlet')[0].classList.remove('hydrated');
		this.document.getElementById('connections-manager').classList.add('scanner-active');
		if (this.document.getElementById('export-popover')) {
			this.document.getElementById('export-popover').style.display = 'none';
		}
	}

	// The hidden elements have to be reset
	private endPrepareLayoutForScan(): void {
		this.document.getElementsByTagName('html')[0].style.backgroundColor = '#fff';
		this.document.getElementsByTagName('ion-router-outlet')[0].classList.add('hydrated');
		this.document.getElementById('connections-manager').classList.remove('scanner-active');
		if (this.document.getElementById('export-popover')) {
			this.document.getElementById('export-popover').style.display = 'block';
		}
	}

	private async testAPIConnection(connectionPath: string): Promise<void> {
		try {
			console.log("check api");
			await firstValueFrom(this.externalTransferApiService.pingApi(connectionPath));
			this.isAPIUrl = true
		} catch (error) {
			if (error.httpStatusCode === HttpStatusCode.ServiceUnavailable) {
				await this.toastService.presentError(
					this.translateService.instant('api.errorMessages.noServerAvailability'),
					ToastDuration.Fast
				);
			}

			if (error.httpStatusCode === HttpStatusCode.NotFound) {
				await this.toastService.presentError(
					this.translateService.instant('api.errorMessages.noInternetConnection'),
					ToastDuration.Fast
				);
			
			}
			this.loadingIndicator.setIsLoading(false);
			await this.scanQrCode()
		}
	}

	private async validateQrCode(result: ScanResult): Promise<void> {
		// connection alias | api key | connection url
		const tokenizedResult: string[] = result.content.split('|');

		const connectionAlias = tokenizedResult[0];
		const connectionUrl = tokenizedResult[1];
		const apiKey = tokenizedResult[2];
		const typoCheck = connectionUrl?.match(regexPatternForConnectionUrl())
		const isMatched = typoCheck.length > 0;
		const isResultValid = connectionAlias !== '' && isMatched;

		if (!isResultValid) {
			this.loadingIndicator.setIsLoading(false);
			this.stopScan();
			await this.toastService.presentError(
				this.translateService.instant('connectionsModal.form.qrCode.errors.notValid'),
				ToastDuration.Slow
			);
			this.modalCtrl.dismiss();
		}

		if (isResultValid) {
			await this.testAPIConnection(connectionUrl);

			

			if(this.isAPIUrl) {
				this.loadingIndicator.setIsLoading(false);
				const apiConnectionId = this.workspaceIdGeneratorService.generateRandomId();
				try {
					await this.apiConnectionsRepository.insert({
						/* eslint-disable-next-line @typescript-eslint/naming-convention */
						connectionId: apiConnectionId,
						serverPath: connectionUrl,
						apiKey: apiKey,
						serverAlias: connectionAlias
					});
					
					if (this.connectionType === ConnectionTypeEnum.includeOptions) {
						this.connectionsModalState.setActive({
							componentName: ConnectionsModalComponentsEnum.optionsConnection,
							connectionId: apiConnectionId
						});
					} else {
						this.modalCtrl.dismiss(apiConnectionId);
					}
				} catch (error) {
					this.loadingIndicator.setIsLoading(false);
					await this.toastService.presentError(
						this.translateService.instant('connectionsModal.form.qrCode.errors.save'),
						ToastDuration.Slow
					);
				}
			}
		}
	}
}